import { Component, ViewChild, OnInit, ElementRef, AfterViewInit, AfterViewChecked } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { finalize, take } from 'rxjs/operators';
import { TicketService } from 'src/app/services/ticket.service';
import { NgForm } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';


declare var bootstrap: any;

@Component({
  selector: 'app-messege',
  templateUrl: './messege.component.html',
  styleUrls: ['./messege.component.scss']
})
export class MessegeComponent implements OnInit {
  tickets: any[] = [];
  
  selectedTicket: any = null;
  ticketMessages: any[] = [];
  groupedMessages: any[] = [];
  isChatEmpty: boolean = true;
  isUserAtBottom: boolean = true;
  isUserScrolling: boolean = false;
  refreshInterval: any = null; // Holds the interval instance
  pollingRate: number = 5000;  // Refresh every 5 seconds (adjust as needed)
  isLoading: boolean = false;
  ticketId!: number;

  @ViewChild('messagesContainer') messagesContainer!: ElementRef;
  @ViewChild('ticketForm') ticketForm!: NgForm;

  constructor(private route: ActivatedRoute, private ticketService: TicketService, private toastrService: ToastrService) {
  }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.ticketId = +params['id'];
      this.loadTicketMessages(this.ticketId);
    });
  }

  startAutoRefresh(): void {
    // Clear any existing interval
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  
    // Start new interval to refresh messages every X seconds
    this.refreshInterval = setInterval(() => {
      if (this.selectedTicket && this.selectedTicket.id) {
        this.refreshMessages(); // Don't force scroll
      }
    }, this.pollingRate);
  }

  refreshMessages(): void {
    if (!this.selectedTicket || !this.selectedTicket.id) return;
  
    const container = this.messagesContainer?.nativeElement;
    if (!container) return;
  
    // Store the previous scroll height before fetching new messages
    const previousScrollHeight = container.scrollHeight;
  
    this.ticketService.getSpecific(this.selectedTicket.id)
      .pipe(take(1))
      .subscribe(
        response => {
          if (response.data) {
            const previousMessageCount = this.ticketMessages.length;
            this.ticketMessages = response.data.messages;
            this.groupMessagesByDate();
  
            // Check if new messages arrived
            const newMessagesAdded = this.ticketMessages.length > previousMessageCount;
  
            // Restore scroll position only if user was not at bottom
            if (!this.isUserAtBottom && newMessagesAdded) {
              setTimeout(() => {
                const newScrollHeight = container.scrollHeight;
                container.scrollTop += newScrollHeight - previousScrollHeight;
              }, 50); // Delay prevents visual flickering
            }
  
            // Auto-scroll only if the user was at the bottom before
            if (newMessagesAdded && this.isUserAtBottom) {
              this.scrollToBottom();
            }
          }
        },
        error => {
          console.error('❌ Error refreshing messages:', error);
        }
      );
  }
                
  loadTicketMessages(ticketId: number): void {
    console.log("📩 Loading messages for ticket ID:", ticketId);
  
    this.isLoading = true; // ✅ Set loading state before API call
    console.log("⏳ isLoading before API call:", this.isLoading);
  
    this.ticketService.getSpecific(ticketId)
      .pipe(
        take(1),
        finalize(() => {
          this.isLoading = false;
          console.log("✅ isLoading set to false after API call:", this.isLoading);
        })
      )
      .subscribe(
        response => {
          if (response.data) {
            this.selectedTicket = { ...response.data, id: ticketId };
            this.ticketMessages = response.data.messages;
            this.groupMessagesByDate();
            this.isChatEmpty = this.ticketMessages.length === 0;
  
            console.log("📩 Messages loaded:", this.ticketMessages);
            
            // Start auto-refresh
            this.startAutoRefresh();
            
            // Scroll to bottom
            this.scrollToBottom(true);
          } else {
            this.toastrService.error("❌ لا يمكن العثور على التذكرة!");
          }
        },
        error => {
          console.error('❌ Error fetching ticket messages:', error);
        }
      );
  }
  goBack(): void {
    window.history.back(); // 🔙 Go back to the previous page
  }
    
  ngOnDestroy(): void {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  }
  
  sendMessage(messageText: string): void {
    if (!messageText.trim()) {
      this.toastrService.warning("⚠️ لا يمكن إرسال رسالة فارغة!");
      return;
    }
  
    if (!this.selectedTicket || !this.selectedTicket.id) {
      this.toastrService.error("❌ يرجى تحديد تذكرة لإرسال الرسائل.");
      return;
    }
  
    this.ticketService.sendMessage(this.selectedTicket.id, messageText)
      .pipe(take(1))
      .subscribe(
        res => {
          if (res.status === 200) {
            // ✅ Append the new message to the chat manually
            const newMessage = {
              id: Date.now(), // Temporary ID (avoid duplicates)
              message: messageText,
              created_at: new Date().toISOString(),
              manager_id: null, // Assume it's a user message
            };
  
            this.ticketMessages.push(newMessage);
            this.groupMessagesByDate();
  
            setTimeout(() => this.scrollToBottom(true), 200); // ✅ Scroll to bottom smoothly
          } else {
            this.toastrService.error("❌ فشل إرسال الرسالة!");
          }
        },
        error => {
          console.error('❌ Error sending message:', error);
          this.toastrService.error("❌ حدث خطأ أثناء إرسال الرسالة!");
        }
      );
  }
          
  groupMessagesByDate(): void {
    const grouped: { [key: string]: any[] } = {}; // Object to store messages grouped by date
  
    // First, remove duplicate messages (in case API returns duplicates)
    const uniqueMessages = Array.from(new Map(this.ticketMessages.map(msg => [msg.id, msg])).values());
  
    // Sort messages by date (ascending order)
    uniqueMessages.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
  
    uniqueMessages.forEach(msg => {
      const date = this.formatDate(msg.created_at); // Ensure a consistent date format
  
      if (!grouped[date]) {
        grouped[date] = []; // Initialize date group
      }
  
      grouped[date].push(msg);
    });
  
    // Assign properly grouped and sorted messages
    this.groupedMessages = Object.keys(grouped)
      .sort((a, b) => new Date(a).getTime() - new Date(b).getTime()) // Sort dates
      .map(date => ({
        date,
        messages: grouped[date]
      }));
  }

  
  formatDate(timestamp: string): string {
    const date = new Date(timestamp);
    return date.toISOString().split('T')[0]; // Returns "YYYY-MM-DD" (fixed)
  }
  
  formatTime(timestamp: string): string {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('EN', { hour: '2-digit', minute: '2-digit' });
  }

  sendMessageOnEnter(input: HTMLInputElement): void {
    if (input.value.trim() !== '') { // Prevent sending empty messages
      this.sendMessage(input.value);
      input.value = ''; // Clear input after sending
    }
  }
  

  ngAfterViewInit() {
    setTimeout(() => {
      this.scrollToBottom(true); // Force scroll to bottom on initial load
    }, 200);
  }
  
ngAfterViewChecked(): void {
  if (this.isUserAtBottom) {
    this.scrollToBottom();
  }
}

scrollToBottom(force: boolean = false): void {
  if (!this.messagesContainer) return;

  const container = this.messagesContainer.nativeElement;

  // If force is true, ensure it scrolls instantly on first load
  if (force) {
    setTimeout(() => {
      container.scrollTop = container.scrollHeight;
    }, 50); // Adjust delay slightly for smoother experience
    return;
  }

  // If the user is at the bottom, allow scrolling down when new messages arrive
  if (this.isUserAtBottom) {
    setTimeout(() => {
      container.scrollTop = container.scrollHeight;
    }, 50); // Small delay prevents flickering
  }
}

onScroll(): void {
  if (!this.messagesContainer) return;

  const container = this.messagesContainer.nativeElement;

  // Calculate if the user is at the bottom
  const isAtBottom = container.scrollTop + container.clientHeight >= container.scrollHeight;

  // If the user manually scrolls up, stop auto-scroll
  if (!isAtBottom) {
    this.isUserAtBottom = false;
  } else {
    this.isUserAtBottom = true;
  }

  // Clear any previous timeout to prevent rapid changes
  clearTimeout((this as any).scrollTimeout);
  (this as any).scrollTimeout = setTimeout(() => {
    this.isUserScrolling = false;
  }, 500); // Reduce flickering by adding a slight delay
}

}
